import { useEffect, useState, useCallback, useMemo } from "react";
import { CiEdit } from "react-icons/ci";
import { BsCloudDownload } from "react-icons/bs";
import { CgSpinner } from "react-icons/cg";
import Grdeskpopup from "./grdeskedit_popup";
import { useDispatch } from "react-redux";
import { PDFDownloadLink } from "@react-pdf/renderer";
import VrfPdfDownload from "../../DummyPdfs/VRFPDFS/vrfpdfDownload";
import NodeInstance from "../../../../Global/nodeAxios";
import { setAppError, setBreadCrumb } from "../../../../store/userSlice";
import { Link } from "react-router-dom";
import FinalVRFDownload from "../../DummyPdfs/VRFPDFS/finalVRF";
import useServerPagination from "../../../../hooks/useServerPagination";
import PaginationControls from "../../../../components/SmartDataTable/PaginationControls";
import SearchFilter from "../../../../components/SmartDataTable/SearchFilter";
import Highlighter from "react-highlight-words";



const NAV_BASE = "p-2 px-4 rounded-md text-white text-sm font-medium";
const NAV_ACTIVE = { backgroundColor: "#1f2937" };
const NAV_INACTIVE = { backgroundColor: "#ef4444" };
const navLink = (isActive) => ({ className: NAV_BASE, style: isActive ? NAV_ACTIVE : NAV_INACTIVE });

export default function Grdesk({ titles = [] }) {
  const dispatch = useDispatch();
  const token = useMemo(() => localStorage.getItem("nodeToken"), []);

  const [searchTerm, setSearchTerm] = useState("");
  const [showPromptElement, setShowPromptElement] = useState(false);
  const [popupData, setPopupData] = useState(null);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [downloadReady, setDownloadReady] = useState(false);

  const { data, paginationState, goToPage, changePageSize } = useServerPagination({
    apiEndpoint: "/dashboard/vrf-visa",
    token,
    defaultPageSize: 50,
    searchTerm,
    searchDebounceMs: 300,
  });

  useEffect(() => {
    dispatch(setBreadCrumb([
      { label: "VISA REQUEST FORM SUMMARY", link: "/dashboard/visa-request-form" },
      { label: "GR DESK SUMMARY" },
    ]));
  }, [dispatch]);

  const handleEditIconClick = useCallback((id, sponsorId, companyName, company_id, status, listData) => {
    setPopupData({ id, name: companyName, sponsor_id: sponsorId, company_id, status, listData });
    setShowPromptElement(true);
  }, []);

  const downloadFile = useCallback(async () => {
    setDownloadLoading(true);
    try {
      const response = await NodeInstance.get(`/dashboard/vrf-visa/download`, { responseType: "blob" });
      if (response.status === 200) {
        const url = window.URL.createObjectURL(response.data);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `GR-DESK-VISA-DETAILS`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        dispatch(setAppError({ msg: "Excel Downloaded", color: "success" }));
      }
    } catch (err) {
      dispatch(setAppError({ msg: err.message }));
    } finally {
      setDownloadLoading(false);
    }
  }, [dispatch]);

  const searchWords = useMemo(() => (searchTerm ? searchTerm.split(/\s+/).filter(Boolean) : []), [searchTerm]);
  const highlight = useCallback((text) => (
    <Highlighter searchWords={searchWords} autoEscape={true} textToHighlight={String(text ?? "")} />
  ), [searchWords]);

  return (
    <>
      <div className="p-2 uppercase">

        {/* Toolbar */}
        <div className="flex-wrap gap-2 w-full items-center flex flex-row-reverse mb-4">
          <SearchFilter
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            placeholder="Search across all columns..."
            isLoading={paginationState.isLoading && searchTerm.length > 0}
          />
          <button
            className="p-2 px-4 rounded-md bg-blue disabled:bg-gray-500 text-white min-w-fit text-sm flex items-center gap-2"
            onClick={downloadFile}
            disabled={downloadLoading || paginationState.isLoading}
          >
            {downloadLoading && <CgSpinner className="animate-spin" />}
            <span>{downloadLoading ? "Downloading..." : "Download VRF"}</span>
          </button>
          <Link to="/dashboard/Visa-return-request/Gr-desk" {...navLink(false)}>VISA RETURNS</Link>
          <span {...navLink(true)}>VISA REQUESTS</span>
        </div>

        {/* Scrollable Table */}
        <div className="overflow-x-auto bg-white rounded-lg shadow max-h-[70vh]">
          <table className="min-w-full">
            <thead className="sticky top-0 z-20 bg-white border-b border-slate-200">
              <tr>
                {titles.map((title, i) => (
                  <th key={i} className="px-6 py-4 text-left text-base font-semibold text-gray-700 uppercase tracking-wider">
                    {title}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white">
              {paginationState.isLoading ? (
                <tr>
                  <td colSpan={titles.length} className="px-6 py-8 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <CgSpinner className="animate-spin text-xl text-blue-500" />
                      <span>Loading data...</span>
                    </div>
                  </td>
                </tr>
              ) : data.length === 0 ? (
                <tr>
                  <td colSpan={titles.length} className="px-6 py-8 text-center text-gray-400">
                    {searchTerm ? `No results found for "${searchTerm}"` : "No Data Found"}
                  </td>
                </tr>
              ) : data.map((listItem, index) => (
                <tr key={listItem.id || index} className="hover:bg-gray-50">
                  <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">{(paginationState.currentPage - 1) * paginationState.pageSize + index + 1}</td>
                  <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">{highlight(listItem?.vrf_reference_number)}</td>
                  <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">{highlight(listItem?.company_name)}</td>
                  <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">{highlight(listItem?.ajeer)}</td>
                  <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">{highlight(listItem?.sponsor_id)}</td>
                  <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">{highlight(listItem?.Nationality)}</td>
                  <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">{highlight(listItem?.visa_stamp_city)}</td>
                  <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">{highlight(listItem?.visa_profession)}</td>
                  <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">{highlight(listItem?.priority_of_issuance)}</td>
                  <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">{highlight(String(listItem?.total_visa ?? ""))}</td>
                  <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">{highlight(listItem.status === "REFUND INITIATED" ? "REFUND" : listItem.status)}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <button
                        className="rounded-md bg-white border border-gray-500 flex items-center justify-center"
                        style={{ width: "2rem", height: "2rem" }}
                        onClick={() => handleEditIconClick(listItem.id, listItem?.sponsor_id, listItem?.company_name, listItem?.company_id, listItem?.status, listItem)}
                        disabled={listItem.status !== "PAYMENT DONE" && listItem.status !== "DRAFT VRF CREATED"}
                      >
                        <CiEdit size={"1rem"} />
                      </button>
                      {listItem.status === "DRAFT VRF CREATED" || listItem.status === "PAYMENT DONE" || listItem.status === "PENDING PAYMENT" ? (
                        <button className="rounded-md bg-white border border-gray-400 flex items-center justify-center" style={{ width: "2rem", height: "2rem", cursor: "pointer" }} onClick={() => setDownloadReady(true)}>
                          <PDFDownloadLink document={<VrfPdfDownload data={listItem} />} fileName="DRAFT_VRF.pdf" onLoadSuccess={() => setDownloadReady(false)}>
                            <BsCloudDownload size={"1rem"} />
                          </PDFDownloadLink>
                        </button>
                      ) : (
                        <button className="rounded-md bg-white border border-gray-400 flex items-center justify-center" style={{ width: "2rem", height: "2rem", cursor: "pointer" }}>
                          <PDFDownloadLink document={<FinalVRFDownload data={listItem} />} fileName="FINAL_VRF.pdf" onLoadSuccess={() => setDownloadReady(false)}>
                            {({ loading }) => (
                              <button onClick={() => setDownloadReady(true)} disabled={loading || downloadReady} style={{ width: "100%", height: "100%" }}>
                                <BsCloudDownload size={"1rem"} />
                              </button>
                            )}
                          </PDFDownloadLink>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        <div className="mt-2">
          <PaginationControls
            currentPage={paginationState.currentPage}
            totalPages={paginationState.totalPages}
            pageSize={paginationState.pageSize}
            totalRecords={paginationState.totalRecords}
            pageSizeOptions={[50, 100, 500, 1000]}
            onPageChange={goToPage}
            onPageSizeChange={changePageSize}
            isLoading={paginationState.isLoading}
          />
        </div>
      </div>

      {showPromptElement && popupData && (
        popupData.status === "PAYMENT DONE" ? (
          <Grdeskpopup showPromptElement={showPromptElement} popupData={popupData} setShowPromptElement={setShowPromptElement} status="PAYMENT DONE" />
        ) : (
          <Grdeskpopup showPromptElement={showPromptElement} popupData={popupData} setShowPromptElement={setShowPromptElement} />
        )
      )}
    </>
  );
}
